import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import {
  defaultGridTuning,
  gridTuningForImageSize,
  type GridTuning,
} from "@/character/draft";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  SHEET_COLUMNS,
  TEMPLATE_SHEET_HEIGHT,
  TEMPLATE_SHEET_WIDTH,
  describeSheetLayout,
  resolveSheetLayout,
  sheetLayoutFromImageLenient,
  supportsTemplateGridLayout,
} from "@/character/grid";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  getSheetUploadCleanPrefs,
  readSheetUploadCleanPrefsFromHost,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import { flipImageDataUrlHorizontal } from "@/character/sheet-flip";
import { downscaleSheetDataUrlIfOverscaled } from "@/character/sheet-scale-down";
import { mountSheetUploadPreview } from "@/character/sheet-upload-preview";
import { downloadCharacterTemplate, downloadMotionTemplate } from "@/character/template";
import type { User } from "firebase/auth";
import type { CharacterProfile } from "@/character/storage";
import type { HomeEconomy } from "@/home/economy";
import { mountPetSkillsPanel } from "@/pet/mount-pet-skills-panel";
import { loadHomeEconomyFromIdb } from "@/home/home-idb";
import { HomeEvents } from "@/home/home-events";
import {
  STAT_IDS,
  STAT_LABEL_TH,
  STAT_PLUS_HINT_TH,
  type StatAllocation,
  type StatId,
} from "@/game/model/character-stats";

const PET_STAT_ICON: Record<StatId, string> = {
  attack: "⚔",
  defense: "🛡",
  critRate: "✦",
  critDamage: "💥",
  dodge: "↷",
  attackSpeed: "⚡",
  moveSpeed: "🏃",
  hpRegen: "♥",
  mana: "💎",
};
import {
  PET_AP_PER_CARD,
  PET_BASE_AP,
} from "@/pet/constants";
import {
  mountPetIdlePreview,
  mountPetSpritePreview,
} from "@/pet/mount-pet-idle-preview";
import {
  equippedPet,
  loadPetState,
  removePetFromState,
  savePetState,
  updatePetInState,
} from "@/pet/firestore-storage";
import {
  allocatePetStatPoint,
  applyPetApCard,
  applyPetSizeCard,
  petApBudget,
  petApRemaining,
} from "@/pet/pet-ap";
import type { PetDefinition, PetPlayerState } from "@/pet/types";
import { syncEquippedPetPublic } from "@/pet/sync-equipped-pet-public";
import { appendPetFromSpritesheet } from "@/pet/create-pet-from-sheet";
import {
  savePetSheetGridCreateSession,
  PET_SHEET_GRID_CREATED_MESSAGE,
  PET_SHEET_GRID_DONE_MESSAGE,
  takePetSheetGridResult,
} from "@/pet/pet-sheet-grid-result";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type MountPetPanelOptions = {
  userId: string;
  user: User;
  profile: CharacterProfile;
  getEconomy: () => HomeEconomy;
  onEconomyUpdated: (next: HomeEconomy) => void;
  /** When set, apply one AP card to this pet on next render. */
  pendingApCardPetId?: string | null;
  onPendingApCardConsumed?: () => void;
};

export type PetPanelHandle = {
  refresh: () => Promise<void>;
  /** Re-render UI (economy counts, create draft) without reloading Firestore. */
  repaint: () => void;
  teardown: () => void;
};

export function mountPetPanel(
  host: HTMLElement,
  options: MountPetPanelOptions,
): PetPanelHandle {
  let state: PetPlayerState = { version: 1, equippedId: null, pets: [] };
  let selectedId: string | null = null;
  let status = "";
  let creating = false;
  let draftName = "สัตว์เลี้ยง";
  let busy = false;
  let createError = "";
  let createWarnings: string[] = [];
  let createSheetSourceDataUrl: string | null = null;
  /** Pre-clean sheet after optional horizontal flip (for re-apply clean / preview). */
  let createSheetRawBeforeClean: string | null = null;
  /** True when uploaded art faces right and must be mirrored to game default (left). */
  let createFlipHorizontal = false;
  let createSpritesheetDataUrl: string | null = null;
  let createGridTuning: GridTuning = defaultGridTuning();
  let stopCreatePreview: (() => void) | undefined;
  let unbindCreateClean: (() => void) | undefined;
  let mountedPreviewSheetUrl: string | null = null;
  let stopCardPreviews: Array<() => void> = [];
  let stopPetSkillsPanel: (() => void) | undefined;
  let statDraftPetId: string | null = null;
  let statDraft: StatAllocation | null = null;
  let detailModalOpen = false;
  let detailTab: "stats" | "skills" = "stats";

  const cloneStatAllocation = (allocation: StatAllocation): StatAllocation => ({
    ...allocation,
  });

  const syncStatDraftForSelected = () => {
    if (!selectedId) {
      statDraft = null;
      statDraftPetId = null;
      return;
    }
    const pet = state.pets.find((s) => s.id === selectedId);
    if (!pet) return;
    if (statDraftPetId !== selectedId || !statDraft) {
      statDraft = cloneStatAllocation(pet.statAllocation);
      statDraftPetId = selectedId;
    }
  };

  const petWithStatDraft = (pet: PetDefinition): PetDefinition => {
    if (statDraftPetId === pet.id && statDraft) {
      return { ...pet, statAllocation: statDraft };
    }
    return pet;
  };

  const isStatDraftDirty = (pet: PetDefinition): boolean => {
    if (statDraftPetId !== pet.id || !statDraft) return false;
    return STAT_IDS.some((id) => statDraft![id] !== pet.statAllocation[id]);
  };

  const resetStatDraftFromPet = (pet: PetDefinition) => {
    statDraft = cloneStatAllocation(pet.statAllocation);
    statDraftPetId = pet.id;
  };

  const clearCardPreviews = () => {
    for (const stop of stopCardPreviews) stop();
    stopCardPreviews = [];
  };

  const mountPetCardPreviews = (equipped: PetDefinition | null) => {
    clearCardPreviews();
    const equippedHost = host.querySelector<HTMLElement>("[data-shadow-equipped-preview]");
    if (equipped && equippedHost) {
      stopCardPreviews.push(
        mountPetSpritePreview(equippedHost, equipped, {
          displaySizePx: 56,
          equippedStyle: true,
          showActionButtons: true,
        }),
      );
    }
    const detailHost = host.querySelector<HTMLElement>("[data-shadow-detail-preview]");
    const detailPet =
      selectedId ? state.pets.find((s) => s.id === selectedId) : undefined;
    if (detailPet && detailHost && detailTab === "stats" && detailModalOpen) {
      stopCardPreviews.push(
        mountPetSpritePreview(detailHost, detailPet, {
          displaySizePx: 108,
          showActionButtons: true,
        }),
      );
    }
    host.querySelectorAll<HTMLElement>("[data-pet-card-preview]").forEach((el) => {
      const id = el.dataset.petCardPreview;
      const pet = id ? state.pets.find((s) => s.id === id) : undefined;
      if (!pet) return;
      stopCardPreviews.push(
        mountPetIdlePreview(el, pet, { displaySizePx: 56 }),
      );
    });
  };

  const syncCreateLayoutWarnings = (probe: HTMLImageElement) => {
    try {
      const layout = resolveSheetLayout(probe, createGridTuning);
      createWarnings = describeSheetLayout(layout).warnings;
    } catch {
      createWarnings = [];
    }
  };

  const rebuildCreateSpritesheetFromSource = async () => {
    if (!createSheetSourceDataUrl) return;
    let dataUrl = createSheetSourceDataUrl;
    if (createFlipHorizontal) {
      dataUrl = await flipImageDataUrlHorizontal(dataUrl);
    }
    createSheetRawBeforeClean = dataUrl;
    const cleanHost = host.querySelector<HTMLElement>("#pet-create-sheet-upload-clean");
    const cleanPrefs = cleanHost
      ? readSheetUploadCleanPrefsFromHost(cleanHost)
      : getSheetUploadCleanPrefs();
    createSpritesheetDataUrl = await applySheetUploadClean(dataUrl, cleanPrefs);
    mountedPreviewSheetUrl = null;
    const probe = new Image();
    await new Promise<void>((resolve, reject) => {
      probe.onload = () => resolve();
      probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
      probe.src = dataUrl;
    });
    syncCreateLayoutWarnings(probe);
  };

  const resetCreateDraft = () => {
    createSheetSourceDataUrl = null;
    createSheetRawBeforeClean = null;
    createFlipHorizontal = false;
    createSpritesheetDataUrl = null;
    createGridTuning = defaultGridTuning();
    createError = "";
    createWarnings = [];
    stopCreatePreview?.();
    stopCreatePreview = undefined;
    unbindCreateClean?.();
    unbindCreateClean = undefined;
    mountedPreviewSheetUrl = null;
  };

  const updateCreateWarningMarkup = () => {
    const warnEl = host.querySelector<HTMLElement>(".shadow-create-warnings");
    if (!warnEl) return;
    if (createWarnings.length === 0) {
      warnEl.remove();
      return;
    }
    warnEl.innerHTML = createWarnings.map((w) => `<li>${escapeHtml(w)}</li>`).join("");
  };

  const mountCreatePreviewIfNeeded = () => {
    const previewHost = host.querySelector<HTMLElement>("#pet-sheet-preview-host");
    if (!previewHost || !createSpritesheetDataUrl) return;
    if (mountedPreviewSheetUrl === createSpritesheetDataUrl && stopCreatePreview) {
      return;
    }
    stopCreatePreview?.();
    stopCreatePreview = undefined;
    mountedPreviewSheetUrl = createSpritesheetDataUrl;
    stopCreatePreview = mountSheetUploadPreview(
      previewHost,
      createSpritesheetDataUrl,
      createGridTuning,
      (gridTuning) => {
        createGridTuning = gridTuning;
        if (createSheetSourceDataUrl) {
          const probe = new Image();
          probe.onload = () => {
            syncCreateLayoutWarnings(probe);
            updateCreateWarningMarkup();
          };
          probe.src = createSheetRawBeforeClean ?? createSheetSourceDataUrl;
        }
      },
      {
        sourceDataUrl: createSheetRawBeforeClean ?? createSpritesheetDataUrl,
        onSheetDataUrlChange: (url) => {
          createSpritesheetDataUrl = url;
          mountedPreviewSheetUrl = url;
        },
        templateAlignMode: true,
      },
    );
  };

  const ingestCreateSheetDataUrl = async (dataUrl: string) => {
    createError = "";
    createWarnings = [];
    status = "กำลังประมวลผลสไปรต์ชีต…";
    render();
    try {
      const downscaled = await downscaleSheetDataUrlIfOverscaled(dataUrl);
      dataUrl = downscaled.dataUrl;
      const probe = new Image();
      await new Promise<void>((resolve, reject) => {
        probe.onload = () => resolve();
        probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
        probe.src = dataUrl;
      });
      sheetLayoutFromImageLenient(probe);
      createSheetSourceDataUrl = dataUrl;
      createFlipHorizontal = false;
      createGridTuning = gridTuningForImageSize(
        probe.naturalWidth,
        probe.naturalHeight,
        supportsTemplateGridLayout,
      );
      await rebuildCreateSpritesheetFromSource();
      const scaleNote =
        downscaled.appliedScale > 1
          ? `ลดขนาด ${downscaled.appliedScale}× เป็นเทมเพลตมาตรฐานแล้ว · `
          : "";
      status = createWarnings.length
        ? `${scaleNote}อัปโหลดแล้ว — ตรวจคำเตือนด้านล่าง · ปรับกริดได้`
        : `${scaleNote}อัปโหลดแล้ว — ปรับกริดด้านล่างได้`;
      render();
    } catch (e) {
      createSheetSourceDataUrl = null;
      createSpritesheetDataUrl = null;
      createGridTuning = defaultGridTuning();
      createWarnings = [];
      createError =
        e instanceof Error
          ? e.message
          : "อัปโหลดไม่สำเร็จ — ใช้เทมเพลต 672×384 px (7 คอลัมน์) หรือสไปรต์ล้วน 576×384 px";
      status = "";
      const fileInput = host.querySelector<HTMLInputElement>("#pet-create-file");
      if (fileInput) fileInput.value = "";
      render();
    }
  };

  const processCreateSheetFile = async (file: File) => {
    const dataUrl = await readImageFileAsDataUrl(file);
    await ingestCreateSheetDataUrl(dataUrl);
  };

  const applyPendingGridSheet = async () => {
    const pending = takePetSheetGridResult();
    if (!pending) return;
    if (!creating) {
      creating = true;
      resetCreateDraft();
    }
    await ingestCreateSheetDataUrl(pending.dataUrl);
    status = "รับชีตจากกริดเฟรมแล้ว — ตรวจตัวอย่างก่อนบันทึก";
    render();
  };

  const persistState = async () => {
    await savePetState(options.userId, state);
    const equipped = equippedPet(state);
    void syncEquippedPetPublic(options.userId, equipped);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.PetChanged, {
        detail: { state, equipped },
      }),
    );
  };

  const renderStatRows = (pet: PetDefinition): string => {
    const view = petWithStatDraft(pet);
    const dirty = isStatDraftDirty(pet);
    const rows = STAT_IDS
      .map((id) => {
        const pts = view.statAllocation[id];
        const canAdd = petApRemaining(view) > 0;
        const hint = STAT_PLUS_HINT_TH[id];
        return `<div class="pet-detail-stat-row" data-stat-id="${id}">
          <span class="pet-detail-stat-icon" aria-hidden="true">${PET_STAT_ICON[id]}</span>
          <span class="pet-detail-stat-label" title="${escapeHtml(hint)}">${escapeHtml(STAT_LABEL_TH[id])}</span>
          <span class="pet-detail-stat-val">${pts}</span>
          <button type="button" class="pet-detail-stat-plus" data-pet-stat="${id}" title="${escapeHtml(hint)}" ${canAdd && !busy ? "" : "disabled"} aria-label="เพิ่ม ${escapeHtml(STAT_LABEL_TH[id])}">+</button>
        </div>`;
      })
      .join("");
    const actions = dirty
      ? `<div class="pet-detail-stat-save-row home-actions">
          <button type="button" class="btn btn-ghost btn-sm" data-shadow-stat-discard ${busy ? "disabled" : ""}>ยกเลิก</button>
          <button type="button" class="btn btn-primary btn-sm" data-shadow-stat-save ${busy ? "disabled" : ""}>บันทึกสเตต</button>
        </div>`
      : "";
    return `<div class="pet-detail-stat-list">${rows}</div>${actions}`;
  };

  const render = () => {
    syncStatDraftForSelected();
    const eco = options.getEconomy();
    const equipped = equippedPet(state);
    const selected = state.pets.find((s) => s.id === selectedId) ?? null;
    const list = state.pets
      .map((s) => {
        const on = s.id === state.equippedId;
        const sel = s.id === selectedId;
        return `<article class="shadow-card${sel ? " shadow-card--sel" : ""}${on ? " shadow-card--equipped" : ""}">
          <button type="button" class="shadow-card-btn" data-pet-open="${escapeHtml(s.id)}" aria-label="เปิด ${escapeHtml(s.name)}">
            <div class="shadow-card-thumb" data-pet-card-preview="${escapeHtml(s.id)}"></div>
            <div class="shadow-card-body">
              <span class="shadow-card-name">${escapeHtml(s.name)}</span>
              <span class="shadow-card-ap hint">AP ${petApRemaining(s)}/${petApBudget(s)}</span>
              ${on ? `<span class="shadow-equipped-tag">ใส่อยู่</span>` : ""}
            </div>
          </button>
        </article>`;
      })
      .join("");

    const equippedCard = equipped
      ? `<article class="shadow-equipped-card" aria-label="สัตว์เลี้ยงที่ใส่อยู่">
          <header class="shadow-equipped-card-head">
            <span class="shadow-equipped-card-eyebrow">ตัวอย่างตอนใส่ในเกม</span>
            <h3 class="shadow-equipped-card-title">${escapeHtml(equipped.name)}</h3>
            <p class="hint shadow-equipped-card-hint">ตามหลังตัวละครในเกม · ขนาดจากบัตรไซต์ (+1%/ใบ)</p>
          </header>
          <div class="shadow-equipped-preview-wrap" data-shadow-equipped-preview></div>
        </article>`
      : `<p class="hint shadow-none-equipped">ยังไม่ได้ใส่สัตว์เลี้ยง — เลือกการ์ดด้านล่างแล้วกดใส่</p>`;

    const actionList = CHARACTER_ACTIONS
      .map(
        (action) =>
          `<li><strong>${escapeHtml(action.templateLabel)}</strong> — ${escapeHtml(action.hint)}</li>`,
      )
      .join("");
    const createPreviewBlock = createSpritesheetDataUrl
      ? `<div id="pet-sheet-preview-host"></div>`
      : `<div class="sheet-preview sheet-preview--empty">ยังไม่ได้อัปโหลดสไปรต์ชีต</div>`;
    const canSubmitCreate = Boolean(
      draftName.trim() && createSpritesheetDataUrl && !busy && !createError,
    );
    const createWarningMarkup = createWarnings.length
      ? `<ul class="grid-panel-warnings shadow-create-warnings" role="alert">
          ${createWarnings.map((w) => `<li>${escapeHtml(w)}</li>`).join("")}
        </ul>`
      : "";
    const createErrorMarkup = createError
      ? `<p class="form-error shadow-create-error" role="alert">${escapeHtml(createError)}</p>`
      : "";

    const createBlock = creating
      ? `
        <div class="shadow-create-form character-card character-card--wide">
          <h4 class="shadow-section-title">สร้างสัตว์เลี้ยงใหม่</h4>
          <p class="hint">
            แต่ละแถว = การเคลื่อนไหว 1 แบบ · ${GRID_COLUMNS} ภาพต่อแถว · รวม ${GRID_ROWS} แถว
            (เทมเพลต ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px · กริด ${SHEET_COLUMNS} คอลัมน์)
          </p>
          <div class="template-steps">
            <ol class="template-step-list">
              <li>กด <strong>โหลดเทมเพลตทั้งแผ่น</strong> (คอลัมน์ 1 = ป้าย IDLE/RUN/…)</li>
              <li>วางสไปรต์ในคอลัมน์ 2–7 — แถวละ ${GRID_COLUMNS} เฟรม</li>
              <li>Export PNG ตามเทมเพลต (หรือ 2×/3×) · อัปโหลดแล้วลากกริดทับแถว action · ซูมดู · ดู loop ก่อนบันทึก</li>
            </ol>
            <ul class="action-row-list">${actionList}</ul>
          </div>
          <div class="template-actions">
            <button type="button" id="pet-download-template" class="btn btn-ghost">โหลดเทมเพลตทั้งแผ่น</button>
            <a class="btn btn-ghost" href="/assets/images/shadow-sprite-sheet-example.png" target="_blank" rel="noopener">
              ดูตัวอย่าง (672×384)
            </a>
            <a class="btn btn-ghost" href="/assets/images/shadow-sprite-sheet-example.png" download="pet-sprite-sheet-672x384.png">
              ดาวน์โหลดไฟล์พร้อมอัปโหลด
            </a>
          </div>
          <div class="template-motion-downloads">
            <p class="hint">เทมเพลตทีละการเคลื่อนไหว (1×${GRID_COLUMNS}):</p>
            <div class="template-actions">
              ${CHARACTER_ACTIONS
                .map(
                  (action) =>
                    `<button type="button" class="btn btn-ghost" data-pet-motion="${action.key}">${action.templateLabel} ×${GRID_COLUMNS}</button>`,
                )
                .join("")}
            </div>
          </div>
          <label class="field">
            <span class="field-label">ชื่อสัตว์เลี้ยง</span>
            <input type="text" class="input" id="pet-create-name" maxlength="48" value="${escapeHtml(draftName)}" />
          </label>
          <div id="pet-create-sheet-upload-clean">${renderSheetUploadCleanControlsHtml("pet-create-clean")}</div>
          <div class="template-actions shadow-create-upload-row">
            <label class="btn btn-ghost sheet-upload">
              อัปโหลดสไปรต์ชีต
              <input type="file" id="pet-create-file" accept="image/png,image/jpeg,image/webp" hidden />
            </label>
            <button type="button" class="btn btn-ghost" id="pet-open-frame-grid">
              อัปโหลดทีละเฟรม (แท็บใหม่)
            </button>
          </div>
          ${
            createSheetSourceDataUrl
              ? `<div class="shadow-facing-tools">
            <p class="hint">ทิศตัวในเกม — หันซ้ายเป็นค่าเริ่มต้น (เหมือนตัวละครหลัก)</p>
            <div class="template-actions">
              <button type="button" class="btn btn-ghost shadow-facing-btn${!createFlipHorizontal ? " shadow-facing-btn--active" : ""}" data-pet-facing="left">ไฟล์หันซ้ายแล้ว</button>
              <button type="button" class="btn btn-ghost shadow-facing-btn${createFlipHorizontal ? " shadow-facing-btn--active" : ""}" data-pet-facing="flip">กลับด้าน (ไฟล์หนขวา)</button>
            </div>
          </div>`
              : ""
          }
          ${createErrorMarkup}
          ${createWarningMarkup}
          ${createPreviewBlock}
          <div class="home-actions">
            <button type="button" class="btn btn-primary" id="pet-create-submit" ${canSubmitCreate ? "" : "disabled"}>บันทึกสัตว์เลี้ยง</button>
            <button type="button" class="btn btn-ghost" id="pet-create-cancel" ${busy ? "disabled" : ""}>ยกเลิก</button>
          </div>
        </div>`
      : `<div class="shadow-start-create-row home-actions">
          <button type="button" class="btn btn-primary" id="pet-start-create" ${eco.petCreateCards < 1 ? "disabled" : ""}>
            สร้างที่นี่
          </button>
          <button type="button" class="btn btn-ghost" id="shadow-start-create-grid" ${eco.petCreateCards < 1 ? "disabled" : ""}>
            สร้างผ่านกริด (แท็บใหม่)
          </button>
          <span class="hint shadow-start-create-hint">บัตรสร้าง ${eco.petCreateCards}</span>
        </div>`;

    const selectedView = selected ? petWithStatDraft(selected) : null;
    const selectedStatDirty = selected ? isStatDraftDirty(selected) : false;

    const renderDetailBody = (pet: PetDefinition, view: PetDefinition) => {
      const statsPanel = `
            <div class="pet-detail-main">
              <div class="pet-detail-left">
                <p class="pet-detail-ap-line">AP <strong>${petApRemaining(view)}</strong> / ${petApBudget(pet)} <span class="pet-detail-ap-sub">(เริ่ม ${PET_BASE_AP} + บัตร ${pet.apCardsApplied}×${PET_AP_PER_CARD})</span>${selectedStatDirty ? ` · <strong class="pet-detail-dirty">ยังไม่บันทึก</strong>` : ""}</p>
                <div class="pet-detail-preview-frame shadow-detail-preview" data-shadow-detail-preview></div>
              </div>
              <div class="pet-detail-right">
                <div class="pet-detail-mana-badge" title="แต้ม AP คงเหลือสำหรับลงสเตต">
                  <span class="pet-detail-mana-label">AP คงเหลือ</span>
                  <span class="pet-detail-mana-val">${petApRemaining(view)}</span>
                </div>
                ${renderStatRows(pet)}
                <div class="pet-detail-action-grid">
                  <button type="button" class="btn btn-ghost pet-detail-action-btn" data-pet-ap="${escapeHtml(pet.id)}" ${eco.petApCards < 1 ? "disabled" : ""}>ใช้บัตร AP (+${PET_AP_PER_CARD})</button>
                  <button type="button" class="btn btn-ghost pet-detail-action-btn" data-pet-size="${escapeHtml(pet.id)}" ${eco.petSizeCards < 1 ? "disabled" : ""}>ใช้บัตรไซต์ (+1%)</button>
                  ${
                    state.equippedId === pet.id
                      ? `<button type="button" class="btn btn-ghost pet-detail-action-btn" data-pet-unequip>ถอดสัตว์เลี้ยง</button>`
                      : `<button type="button" class="btn btn-primary pet-detail-action-btn" data-pet-equip="${escapeHtml(pet.id)}">ใส่สัตว์เลี้ยง</button>`
                  }
                  <button type="button" class="btn btn-danger pet-detail-action-btn pet-detail-action-btn--danger" data-pet-delete="${escapeHtml(pet.id)}" ${busy ? "disabled" : ""}>ลบสัตว์เลี้ยง</button>
                </div>
              </div>
            </div>`;
      return `
        <nav class="pet-detail-tabs char-sheet-tabs char-sheet-tabs--game" role="tablist" aria-label="จัดการสัตว์เลี้ยง">
          <button type="button" class="char-sheet-tab${detailTab === "stats" ? " char-sheet-tab--active" : ""}" role="tab" aria-selected="${detailTab === "stats" ? "true" : "false"}" data-pet-detail-tab="stats">สเตตัส</button>
          <button type="button" class="char-sheet-tab${detailTab === "skills" ? " char-sheet-tab--active" : ""}" role="tab" aria-selected="${detailTab === "skills" ? "true" : "false"}" data-pet-detail-tab="skills">สกิล</button>
        </nav>
        <div class="pet-detail-sheet pet-detail-sheet--${detailTab}">
          ${
            detailTab === "stats"
              ? `<div class="pet-detail-panel pet-detail-panel--stats" role="tabpanel">${statsPanel}</div>`
              : `<div class="pet-detail-panel pet-detail-panel--skills" role="tabpanel"><div id="pet-skills-host" class="pet-skills-host pet-detail-skills-host"></div></div>`
          }
        </div>`;
    };

    const detailModal =
      detailModalOpen && selected && selectedView
        ? `
      <div class="pet-detail-modal home-skill-editor-modal" role="presentation">
        <button type="button" class="home-skill-editor-backdrop pet-detail-modal-backdrop" data-pet-detail-close aria-label="ปิด"></button>
        <div class="pet-detail-modal-dialog home-skill-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="pet-detail-modal-title">
          <header class="pet-detail-modal-head">
            <div>
              <p class="pet-detail-modal-eyebrow">สัตว์เลี้ยง</p>
              <h3 id="pet-detail-modal-title" class="shadow-detail-title">${escapeHtml(selected.name)}</h3>
            </div>
            <button type="button" class="skill-editor-close pet-detail-modal-close" data-pet-detail-close aria-label="ปิด">×</button>
          </header>
          <div class="pet-detail-modal-body${selectedStatDirty ? " pet-detail-modal-body--dirty" : ""}">
            ${renderDetailBody(selected, selectedView)}
          </div>
        </div>
      </div>`
        : "";

    host.innerHTML = `
      <section class="shadow-panel" aria-label="สัตว์เลี้ยง">
        <p class="hint">คลังสะสม · กดการ์ดเพื่อจัดการสเตตและสกิล · ใส่ได้ 1 ตัว</p>
        ${equippedCard}
        <h4 class="shadow-section-title">คลังสะสม (${state.pets.length})</h4>
        <div class="shadow-collection-grid pet-collection-grid">${list || `<p class="hint pet-collection-empty">ยังไม่มีสัตว์เลี้ยง</p>`}</div>
        ${createBlock}
        ${status ? `<p class="hint shadow-status">${escapeHtml(status)}</p>` : ""}
      </section>
      ${detailModal}`;

    document.body.classList.toggle("pet-detail-modal-open", detailModalOpen);

    stopCreatePreview?.();
    stopCreatePreview = undefined;
    unbindCreateClean?.();
    unbindCreateClean = undefined;

    if (creating) {
      const cleanHost = host.querySelector<HTMLElement>("#pet-create-sheet-upload-clean");
      if (cleanHost) {
        unbindCreateClean = bindSheetUploadCleanControls(cleanHost, () => {
          void (async () => {
            try {
              await rebuildCreateSpritesheetFromSource();
              render();
            } catch {
              /* ignore */
            }
          })();
        });
      }
      mountCreatePreviewIfNeeded();
    }
    mountPetCardPreviews(equipped);

    stopPetSkillsPanel?.();
    stopPetSkillsPanel = undefined;
    const skillsHost = host.querySelector<HTMLElement>("#pet-skills-host");
    if (detailModalOpen && detailTab === "skills" && selected && skillsHost) {
      const skillsHandle = mountPetSkillsPanel(skillsHost, {
        user: options.user,
        profile: options.profile,
        getPet: () => state.pets.find((p) => p.id === selected.id) ?? selected,
        getEconomy: options.getEconomy,
        onPetUpdated: (pet) => {
          state = updatePetInState(state, pet);
          void persistState();
        },
        onEconomyUpdated: options.onEconomyUpdated,
      });
      stopPetSkillsPanel = skillsHandle?.teardown;
    }
  };

  const applySizeToPet = async (petId: string) => {
    const pet = state.pets.find((s) => s.id === petId);
    if (!pet) return;
    const result = applyPetSizeCard(options.getEconomy(), pet);
    if (!result.ok) {
      status = result.reason;
      render();
      return;
    }
    options.onEconomyUpdated(result.economy);
    state = updatePetInState(state, result.pet);
    busy = true;
    try {
      await persistState();
      status = `ใช้บัตรไซต์กับ ${result.pet.name} แล้ว (+${result.pet.sizeCardsApplied}%)`;
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const applyApToPet = async (petId: string) => {
    const pet = state.pets.find((s) => s.id === petId);
    if (!pet) return;
    const result = applyPetApCard(options.getEconomy(), pet);
    if (!result.ok) {
      status = result.reason;
      render();
      return;
    }
    options.onEconomyUpdated(result.economy);
    state = updatePetInState(state, result.pet);
    if (selectedId === result.pet.id) {
      resetStatDraftFromPet(result.pet);
    }
    busy = true;
    try {
      await persistState();
      status = `ใช้บัตร AP กับ ${result.pet.name} แล้ว`;
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const tryCloseDetailModal = (): boolean => {
    if (!detailModalOpen) return true;
    const current = selectedId ? state.pets.find((s) => s.id === selectedId) : undefined;
    if (current && isStatDraftDirty(current)) {
      if (!window.confirm("ยังไม่บันทึกสเตต — ปิดและทิ้งการแก้ไข?")) {
        return false;
      }
      resetStatDraftFromPet(current);
    }
    detailModalOpen = false;
    detailTab = "stats";
    return true;
  };

  const trySwitchDetailTab = (next: "stats" | "skills"): boolean => {
    if (detailTab === next) return true;
    if (detailTab === "stats" && next === "skills") {
      const current = selectedId ? state.pets.find((s) => s.id === selectedId) : undefined;
      if (current && isStatDraftDirty(current)) {
        if (!window.confirm("ยังไม่บันทึกสเตต — เปลี่ยนแท็บและทิ้งการแก้ไข?")) {
          return false;
        }
        resetStatDraftFromPet(current);
      }
    }
    detailTab = next;
    return true;
  };

  const openPetDetail = (nextId: string) => {
    const current = selectedId ? state.pets.find((s) => s.id === selectedId) : undefined;
    if (
      detailModalOpen &&
      selectedId &&
      nextId !== selectedId &&
      current &&
      isStatDraftDirty(current) &&
      !window.confirm("ยังไม่บันทึกสเตต — เปลี่ยนสัตว์เลี้ยงและทิ้งการแก้ไข?")
    ) {
      return;
    }
    selectedId = nextId;
    statDraftPetId = null;
    detailModalOpen = true;
    detailTab = "stats";
    status = "";
    render();
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const tabBtn = target.closest<HTMLElement>("[data-pet-detail-tab]");
    if (tabBtn?.dataset.petDetailTab === "stats" || tabBtn?.dataset.petDetailTab === "skills") {
      if (trySwitchDetailTab(tabBtn.dataset.petDetailTab as "stats" | "skills")) {
        render();
      }
      return;
    }
    if (target.closest("[data-pet-detail-close]")) {
      if (tryCloseDetailModal()) render();
      return;
    }
    const openBtn = target.closest<HTMLElement>("[data-pet-open]");
    if (openBtn?.dataset.petOpen) {
      openPetDetail(openBtn.dataset.petOpen);
      return;
    }
    if (target.closest("#pet-start-create")) {
      creating = true;
      status = "";
      createError = "";
      createWarnings = [];
      resetCreateDraft();
      render();
      return;
    }
    if (target.closest("#shadow-start-create-grid")) {
      const eco = options.getEconomy();
      if (eco.petCreateCards < 1) {
        status = "ไม่มีบัตรสร้างสัตว์เลี้ยง";
        render();
        return;
      }
      savePetSheetGridCreateSession(draftName);
      window.open("/pet/sheet-grid?create=1", "_blank", "noopener");
      status =
        "เปิดแท็บกริดแล้ว — กรอกเฟรมครบแล้วกด «สร้างสัตว์เลี้ยง» ในแท็บนั้นได้เลย";
      render();
      return;
    }
    if (target.closest("#pet-create-cancel")) {
      creating = false;
      resetCreateDraft();
      render();
      return;
    }
    if (target.closest("#pet-create-submit")) {
      void submitCreate();
      return;
    }
    if (target.closest("#pet-download-template")) {
      downloadCharacterTemplate();
      return;
    }
    if (target.closest("#pet-open-frame-grid")) {
      window.open("/pet/sheet-grid", "_blank", "noopener");
      status =
        "เปิดแท็บกริดแล้ว — กรอกครบ 24 เฟรมแล้วกด «ใช้ชีตนี้และกลับ» ชีตจะโหลดที่นี่อัตโนมัติ";
      render();
      return;
    }
    const motionBtn = target.closest<HTMLElement>("[data-pet-motion]");
    if (motionBtn?.dataset.petMotion) {
      downloadMotionTemplate(motionBtn.dataset.petMotion as CharacterActionKey);
      return;
    }
    const facingBtn = target.closest<HTMLElement>("[data-pet-facing]");
    if (facingBtn?.dataset.petFacing && createSheetSourceDataUrl) {
      const mode = facingBtn.dataset.petFacing;
      const nextFlip = mode === "flip";
      if (nextFlip === createFlipHorizontal) return;
      createFlipHorizontal = nextFlip;
      void (async () => {
        busy = true;
        status = "กำลังกลับด้านสไปรต์…";
        render();
        try {
          await rebuildCreateSpritesheetFromSource();
          status = createFlipHorizontal
            ? "กลับด้านแล้ว — ตัวหันซ้ายตามระบบ"
            : "ใช้ทิศทางจากไฟล์ต้นฉบับ";
        } catch (e) {
          createError = e instanceof Error ? e.message : "กลับด้านไม่สำเร็จ";
          status = "";
        } finally {
          busy = false;
          render();
        }
      })();
      return;
    }
    const equipBtn = target.closest<HTMLElement>("[data-pet-equip]");
    if (equipBtn?.dataset.petEquip) {
      void equip(equipBtn.dataset.petEquip);
      return;
    }
    if (target.closest("[data-pet-unequip]")) {
      void equip(null);
      return;
    }
    const apBtn = target.closest<HTMLElement>("[data-pet-ap]");
    if (apBtn?.dataset.petAp) {
      void applyApToPet(apBtn.dataset.petAp);
      return;
    }
    const sizeBtn = target.closest<HTMLElement>("[data-pet-size]");
    if (sizeBtn?.dataset.petSize) {
      void applySizeToPet(sizeBtn.dataset.petSize);
      return;
    }
    const statBtn = target.closest<HTMLElement>("[data-pet-stat]");
    if (statBtn?.dataset.petStat && selectedId) {
      bumpStatDraft(statBtn.dataset.petStat as StatId);
      return;
    }
    if (target.closest("[data-shadow-stat-discard]")) {
      discardStatDraft();
      return;
    }
    if (target.closest("[data-shadow-stat-save]")) {
      void saveStatDraft();
      return;
    }
    const deleteBtn = target.closest<HTMLElement>("[data-pet-delete]");
    if (deleteBtn?.dataset.petDelete) {
      void deletePet(deleteBtn.dataset.petDelete);
    }
  };

  const deletePet = async (petId: string) => {
    const pet = state.pets.find((s) => s.id === petId);
    if (!pet) return;
    const equippedNote =
      state.equippedId === petId ? "\n(จะถอดออกจากตัวละครด้วย)" : "";
    if (
      !window.confirm(
        `ลบสัตว์เลี้ยง "${pet.name}" ถาวร?\nAP และบัตรที่ใช้ไปแล้วไม่คืน${equippedNote}`,
      )
    ) {
      return;
    }
    busy = true;
    render();
    try {
      state = removePetFromState(state, petId);
      if (selectedId === petId) {
        selectedId = state.pets[0]?.id ?? null;
        detailModalOpen = false;
      }
      await persistState();
      status = `ลบ ${pet.name} แล้ว`;
    } catch {
      status = "ลบไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const equip = async (petId: string | null) => {
    state = { ...state, equippedId: petId };
    busy = true;
    try {
      await persistState();
      status = petId ? "ใส่สัตว์เลี้ยงแล้ว" : "ถอดสัตว์เลี้ยงแล้ว";
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const bumpStatDraft = (statId: StatId) => {
    if (!selectedId || busy) return;
    const pet = state.pets.find((s) => s.id === selectedId);
    if (!pet) return;
    syncStatDraftForSelected();
    const view = petWithStatDraft(pet);
    const next = allocatePetStatPoint(view, statId);
    if (!next) return;
    statDraft = cloneStatAllocation(next.statAllocation);
    statDraftPetId = pet.id;
    render();
  };

  const discardStatDraft = () => {
    if (!selectedId) return;
    const pet = state.pets.find((s) => s.id === selectedId);
    if (!pet) return;
    resetStatDraftFromPet(pet);
    status = "";
    render();
  };

  const saveStatDraft = async () => {
    if (!selectedId || busy) return;
    const pet = state.pets.find((s) => s.id === selectedId);
    if (!pet || !statDraft || !isStatDraftDirty(pet)) return;
    const next = { ...pet, statAllocation: cloneStatAllocation(statDraft) };
    state = updatePetInState(state, next);
    busy = true;
    render();
    try {
      await persistState();
      resetStatDraftFromPet(next);
      status = "บันทึกสเตตสัตว์เลี้ยงแล้ว";
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const submitCreate = async () => {
    const eco = options.getEconomy();
    if (eco.petCreateCards < 1) {
      status = "ไม่มีบัตรสร้างสัตว์เลี้ยง";
      render();
      return;
    }
    const nameInput = host.querySelector<HTMLInputElement>("#pet-create-name");
    const name = (nameInput?.value ?? draftName).trim() || "สัตว์เลี้ยง";
    if (!createSpritesheetDataUrl) {
      createError = "อัปโหลดสไปรต์ชีตและดูตัวอย่างก่อนบันทึก";
      status = "";
      render();
      return;
    }
    createError = "";
    busy = true;
    status = "กำลังประมวลผล…";
    render();
    try {
      await rebuildCreateSpritesheetFromSource();
      const sheetUrl = createSpritesheetDataUrl;
      if (!sheetUrl) {
        createError = "อัปโหลดสไปรต์ชีตและดูตัวอย่างก่อนบันทึก";
        status = "";
        return;
      }
      const result = await appendPetFromSpritesheet(state, eco, {
        name,
        sheetDataUrl: sheetUrl,
        gridTuning: createGridTuning,
      });
      if (!result.ok) {
        createError = result.reason;
        status = "";
        return;
      }
      state = result.state;
      selectedId = result.pet.id;
      options.onEconomyUpdated(result.economy);
      await persistState();
      creating = false;
      resetCreateDraft();
      status = `สร้าง ${result.pet.name} แล้ว`;
    } catch (e) {
      createError = e instanceof Error ? e.message : "สร้างสัตว์เลี้ยงไม่สำเร็จ";
      status = "";
    } finally {
      busy = false;
      render();
    }
  };

  const onHostChange = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.id !== "pet-create-file") return;
    if (!creating) return;
    const file = input.files?.[0];
    if (!file) return;
    void processCreateSheetFile(file);
  };

  const onHostInput = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.id !== "pet-create-name") return;
    draftName = input.value;
  };

  const onGridMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === PET_SHEET_GRID_DONE_MESSAGE) {
      void applyPendingGridSheet();
      return;
    }
    if (event.data?.type === PET_SHEET_GRID_CREATED_MESSAGE) {
      const name =
        typeof event.data.name === "string" ? event.data.name : "สัตว์เลี้ยง";
      void (async () => {
        creating = false;
        resetCreateDraft();
        const eco = await loadHomeEconomyFromIdb(options.userId);
        if (eco) options.onEconomyUpdated(eco);
        await refresh();
        status = `สร้าง ${name} จากแท็บกริดแล้ว`;
        render();
      })();
    }
  };

  const onGridFocus = () => {
    void applyPendingGridSheet();
  };

  host.addEventListener("click", onClick);
  host.addEventListener("change", onHostChange);
  host.addEventListener("input", onHostInput);
  window.addEventListener("message", onGridMessage);
  window.addEventListener("focus", onGridFocus);
  void applyPendingGridSheet();

  const refresh = async () => {
    state = await loadPetState(options.userId);
    statDraftPetId = null;
    render();
    const pending = options.pendingApCardPetId;
    if (pending && state.pets.some((s) => s.id === pending)) {
      selectedId = pending;
      detailModalOpen = true;
      render();
      await applyApToPet(pending);
      options.onPendingApCardConsumed?.();
    }
  };

  void refresh();

  return {
    refresh,
    repaint: () => render(),
    teardown: () => {
      host.removeEventListener("click", onClick);
      host.removeEventListener("change", onHostChange);
      host.removeEventListener("input", onHostInput);
      window.removeEventListener("message", onGridMessage);
      window.removeEventListener("focus", onGridFocus);
      document.body.classList.remove("pet-detail-modal-open");
      resetCreateDraft();
      clearCardPreviews();
      stopPetSkillsPanel?.();
      host.replaceChildren();
    },
  };
}
